import { useECommerceProduct } from "@omni/components/components/Product/ECommerce/hooks/useECommerceProduct.hook";
import { StorePickerModal } from "@omni/components/components/Store/StorePicker/storePickerModal.container";
import { StorePickerType } from "@omni/components/components/Store/StorePicker/types/storePickerType.enum";
import { useGranite } from "@omni/components/redux/modules/value/translations/translations.provider";
import { GetStoreStrategy } from "@omni/components/services/store.service";
import { Config } from "@omni/components/utils/config.utils";
import { useAssociatedStore } from "@omni/components/utils/hooks/store/useAssociatedStore.hook";
import { useSingleStoreCheck } from "@omni/components/utils/hooks/store/useSingleStoreCheck.hook";
import { useStore } from "@omni/components/utils/hooks/store/useStore.hook";
import { hasNoStock } from "@omni/components/utils/productAvailability";
import { getTagIdFor } from "@omni/components/utils/tagging/tag.utils";
import { CloudStoreType } from "@omni/types/api/store/cloud/shared/enums/storeType.enum";
import { Anchor } from "@omni/ui/components/Anchor/anchor.component";
import { Button } from "@omni/ui/components/Button/button.component";
import { Divider } from "@omni/ui/components/Divider/divider.component";
import { cn } from "@omni/utils/styles/tw.merge";
import { useCallback, useState } from "react";

import { DeliveryTypeTitle } from "@/components/Product/ECommerce/components/driveIn/components/title";

import { DriveInComponent } from "./driveIn.component";

type Props = {
    className?: string;
};

export const DriveIn = ({ className }: Props) => {
    const { t } = useGranite();
    const { sku, isBoxArticle } = useECommerceProduct();
    const [isStorePickerOpen, setIsStorePickerOpen] = useState(false);
    const singleStoreCode = useSingleStoreCheck();

    const { store } = useStore({
        sku,
        strategy: GetStoreStrategy.DriveIn
    });

    const { associatedStore } = useAssociatedStore({
        sku,
        strategy: GetStoreStrategy.Session,
        type: CloudStoreType.DriveIn
    });

    const driveInStore = store || associatedStore;

    const handleOnClose = useCallback(() => setIsStorePickerOpen(false), []);

    return (
        <div
            className={cn(
                "tw:w-full tw:bg-grey tw:rounded-xl tw:p-4",
                "tw:md:bg-transparent tw:md:border tw:md:border-tertiary",
                className
            )}
            {...getTagIdFor("pdp-delivery-drive-in")}
        >
            <DeliveryTypeTitle
                title={t("ecommerce.title.delivery.driveIn")}
                subTitle={t("ecommerce.subtitle.delivery.driveIn")}
            />
            {driveInStore && <Divider />}
            <DriveInComponent store={driveInStore} />
            <div className="tw:flex tw:justify-between">
                {!singleStoreCode && (
                    <Button
                        test={`deliveryItem-drive-in-button-showAvailability`}
                        color={"link"}
                        className={cn("tw:p-0 tw:block tw:mt-2")}
                        type="button"
                        onClick={() => {
                            setIsStorePickerOpen(true);
                        }}
                    >
                        {t(
                            isBoxArticle
                                ? "general.ecommerce.link.box.showStores"
                                : "general.ecommerce.link.showAvailability"
                        )}
                        <i className={cn("las la-angle-right", "tw:mr-1")} />
                    </Button>
                )}
                {driveInStore &&
                    !hasNoStock(
                        driveInStore.productAvailability
                            ?.realTimeStockAvailable,
                        driveInStore.productAvailability?.stockLevel,
                        driveInStore.productAvailability?.realTimeStock
                    ) && (
                        <Anchor
                            color={"link"}
                            data-test={`deliveryItem-drive-in-pick-up-supplierInformation`}
                            className={cn("tw:m-0 tw:p-0 tw:block tw:mt-2")}
                            href={`${Config.pages["stores"]}${driveInStore.code}/#drive-in-info`}
                        >
                            {t("ecommerce.label.driveInPickUp.infoLink")}
                            <i className={cn("las la-angle-right tw:mr-1")} />
                        </Anchor>
                    )}
            </div>
            <StorePickerModal
                isOpen={isStorePickerOpen}
                type={StorePickerType.SELECT}
                storeCode={driveInStore?.code ?? null}
                displayStockLevel={!isBoxArticle}
                submitButtonText={t("general.storepicker.button.selectStore")}
                infoLink={null}
                onSubmit={(_, close) => close?.()}
                onClose={handleOnClose}
                filterStoreType={CloudStoreType.DriveIn}
            />
        </div>
    );
};
